import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, put, del } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Badge } from '@/components/atoms/Badge';
import { Skeleton } from '@/components/atoms/Skeleton';
import { useScrollLock } from '@/hooks/useScrollLock';

interface Board {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  is_visible: boolean;
  is_locked: boolean;
  /** 게시글 보관 기간(일). null = 영구 보관, 양의 정수 = N일 경과 후 정리 대상 */
  retention_days: number | null;
}

/** 보관 기간 라벨 — null/0 → "영구", 양수 → "N일" */
function retentionLabel(days: number | null | undefined): string {
  return days == null ? '영구' : `${days}일`;
}
interface Category {
  id: number;
  board_id: number;
  name: string;
  sort_order: number;
  is_active: boolean;
}

export function BoardsPage() {
  const qc = useQueryClient();
  const { data: boards, isLoading } = useQuery({
    queryKey: ['admin', 'boards'],
    queryFn: () => get<{ boards: Board[] }>('/admin/boards'),
  });

  const [selectedBoard, setSelectedBoard] = useState<Board | null>(null);
  const [boardEditing, setBoardEditing] = useState<Partial<Board> | null>(null);
  const [catEditing, setCatEditing] = useState<Partial<Category> | null>(null);
  useScrollLock(boardEditing !== null || catEditing !== null);

  const { data: categories } = useQuery({
    queryKey: ['admin', 'boards', selectedBoard?.id, 'categories'],
    queryFn: () =>
      get<{ categories: Category[] }>(`/admin/boards/${selectedBoard!.id}/categories`),
    enabled: !!selectedBoard,
  });

  const saveBoardMut = useMutation({
    mutationFn: async () => {
      if (!boardEditing) return;
      const body = {
        slug: boardEditing.slug,
        name: boardEditing.name,
        description: boardEditing.description ?? null,
        sort_order: boardEditing.sort_order ?? 0,
        is_active: boardEditing.is_active ?? true,
        is_visible: boardEditing.is_visible ?? true,
        // 빈 값/0 → null (영구 보관), 양의 정수 → 그대로
        retention_days:
          boardEditing.retention_days == null || boardEditing.retention_days <= 0
            ? null
            : boardEditing.retention_days,
      };
      if (boardEditing.id) await put(`/admin/boards/${boardEditing.id}`, body);
      else await post('/admin/boards', body);
    },
    onSuccess: () => {
      setBoardEditing(null);
      qc.invalidateQueries({ queryKey: ['admin', 'boards'] });
    },
  });

  const saveCatMut = useMutation({
    mutationFn: async () => {
      if (!catEditing || !selectedBoard) return;
      const body = {
        name: catEditing.name,
        sort_order: catEditing.sort_order ?? 0,
        is_active: catEditing.is_active ?? true,
      };
      if (catEditing.id) await put(`/admin/boards/categories/${catEditing.id}`, body);
      else await post(`/admin/boards/${selectedBoard.id}/categories`, body);
    },
    onSuccess: () => {
      setCatEditing(null);
      qc.invalidateQueries({ queryKey: ['admin', 'boards', selectedBoard?.id, 'categories'] });
    },
  });

  const deactivateBoardMut = useMutation({
    mutationFn: (id: number) => del(`/admin/boards/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'boards'] }),
  });
  const deactivateCatMut = useMutation({
    mutationFn: (id: number) => del(`/admin/boards/categories/${id}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['admin', 'boards', selectedBoard?.id, 'categories'] }),
  });

  return (
    <div className="w-full">
      <h1 className="mb-6 text-2xl font-extrabold">게시판/카테고리 관리</h1>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* 게시판 리스트 */}
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-bold">게시판</h2>
            <Button size="sm" onClick={() => setBoardEditing({ is_active: true, is_visible: true })}>
              + 새 게시판
            </Button>
          </div>
          {isLoading ? (
            <Skeleton className="h-80" />
          ) : (
            <ul className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
              {boards?.boards.map((b) => (
                <li
                  key={b.id}
                  onClick={() => setSelectedBoard(b)}
                  className={`flex cursor-pointer items-center gap-2 border-t border-[var(--color-border)] p-3 first:border-t-0 hover:bg-[var(--color-bg)] ${
                    selectedBoard?.id === b.id ? 'bg-[var(--color-bg)]' : ''
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{b.name}</span>
                      <code className="text-xs text-[var(--color-text-sub)]">/{b.slug}</code>
                      {!b.is_active && <Badge variant="default">비활성</Badge>}
                      {!b.is_visible && <Badge variant="outline">숨김</Badge>}
                      {b.is_locked && <Badge variant="fall">잠금</Badge>}
                      {/* 보관 기간 — 영구는 회색(default), 일수 지정은 primary 톤 */}
                      <Badge variant={b.retention_days == null ? 'default' : 'primary'}>
                        보관 {retentionLabel(b.retention_days)}
                      </Badge>
                    </div>
                    {b.description && (
                      <p className="mt-0.5 text-xs text-[var(--color-text-sub)]">{b.description}</p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      setBoardEditing(b);
                    }}
                  >
                    수정
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm(`"${b.name}" 게시판을 비활성화할까요?`)) {
                        deactivateBoardMut.mutate(b.id);
                      }
                    }}
                  >
                    비활성
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* 카테고리 */}
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-bold">
              {selectedBoard ? `${selectedBoard.name} 카테고리` : '카테고리'}
            </h2>
            {selectedBoard && (
              <Button size="sm" onClick={() => setCatEditing({ is_active: true })}>
                + 새 카테고리
              </Button>
            )}
          </div>
          {!selectedBoard ? (
            <p className="rounded-md border border-dashed border-[var(--color-border)] p-6 text-center text-sm text-[var(--color-text-sub)]">
              좌측에서 게시판을 선택하세요.
            </p>
          ) : (
            <ul className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
              {categories?.categories.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center gap-2 border-t border-[var(--color-border)] p-3 first:border-t-0"
                >
                  <span className="flex-1">{c.name}</span>
                  <span className="text-xs text-[var(--color-text-sub)]">order={c.sort_order}</span>
                  {!c.is_active && <Badge variant="default">비활성</Badge>}
                  <Button size="sm" variant="ghost" onClick={() => setCatEditing(c)}>
                    수정
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => {
                      if (confirm(`"${c.name}" 카테고리를 비활성화할까요?`)) {
                        deactivateCatMut.mutate(c.id);
                      }
                    }}
                  >
                    비활성
                  </Button>
                </li>
              ))}
              {categories?.categories.length === 0 && (
                <li className="p-6 text-center text-sm text-[var(--color-text-sub)]">
                  카테고리가 없어요.
                </li>
              )}
            </ul>
          )}
        </section>
      </div>

      {/* 게시판 모달 */}
      {boardEditing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setBoardEditing(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md space-y-3 rounded-2xl bg-[var(--color-surface)] p-6"
          >
            <h3 className="text-lg font-bold">
              {boardEditing.id ? '게시판 수정' : '새 게시판'}
            </h3>
            <Input
              placeholder="slug (영소문자/숫자/하이픈)"
              value={boardEditing.slug ?? ''}
              onChange={(e) => setBoardEditing({ ...boardEditing, slug: e.target.value })}
            />
            <Input
              placeholder="이름"
              value={boardEditing.name ?? ''}
              onChange={(e) => setBoardEditing({ ...boardEditing, name: e.target.value })}
            />
            <Input
              placeholder="설명"
              value={boardEditing.description ?? ''}
              onChange={(e) => setBoardEditing({ ...boardEditing, description: e.target.value })}
            />
            <Input
              type="number"
              placeholder="정렬순서"
              value={boardEditing.sort_order ?? 0}
              onChange={(e) =>
                setBoardEditing({ ...boardEditing, sort_order: Number(e.target.value) })
              }
            />
            {/* 보관 기간 (일) — 빈 값 = 영구 보관(null). 자동 삭제 cron 은 추후 별도 적용. */}
            <div>
              <Input
                type="number"
                min={1}
                placeholder="보관 기간 (일) — 비워두면 영구 보관"
                value={boardEditing.retention_days ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setBoardEditing({
                    ...boardEditing,
                    retention_days: v === '' ? null : Math.max(1, Number(v)),
                  });
                }}
              />
              <p className="mt-1 text-xs text-[var(--color-text-sub)]">
                현재: <strong>{retentionLabel(boardEditing.retention_days)}</strong>
                {' '}· 비워두면 영구 보관 (예: 공시 분석 30, 증시뉴스 30)
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={boardEditing.is_active ?? true}
                onChange={(e) => setBoardEditing({ ...boardEditing, is_active: e.target.checked })}
              />
              활성
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={boardEditing.is_visible ?? true}
                onChange={(e) => setBoardEditing({ ...boardEditing, is_visible: e.target.checked })}
              />
              표시
            </label>
            <div className="flex gap-2 pt-2">
              <Button variant="ghost" fullWidth onClick={() => setBoardEditing(null)}>
                취소
              </Button>
              <Button
                fullWidth
                disabled={!boardEditing.slug || !boardEditing.name}
                loading={saveBoardMut.isPending}
                onClick={() => saveBoardMut.mutate()}
              >
                저장
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 카테고리 모달 */}
      {catEditing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setCatEditing(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md space-y-3 rounded-2xl bg-[var(--color-surface)] p-6"
          >
            <h3 className="text-lg font-bold">
              {catEditing.id ? '카테고리 수정' : '새 카테고리'}
            </h3>
            <Input
              placeholder="이름"
              value={catEditing.name ?? ''}
              onChange={(e) => setCatEditing({ ...catEditing, name: e.target.value })}
            />
            <Input
              type="number"
              placeholder="정렬순서"
              value={catEditing.sort_order ?? 0}
              onChange={(e) =>
                setCatEditing({ ...catEditing, sort_order: Number(e.target.value) })
              }
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={catEditing.is_active ?? true}
                onChange={(e) => setCatEditing({ ...catEditing, is_active: e.target.checked })}
              />
              활성
            </label>
            <div className="flex gap-2 pt-2">
              <Button variant="ghost" fullWidth onClick={() => setCatEditing(null)}>
                취소
              </Button>
              <Button
                fullWidth
                disabled={!catEditing.name}
                loading={saveCatMut.isPending}
                onClick={() => saveCatMut.mutate()}
              >
                저장
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
